import { useEffect, useState, type ReactNode } from 'react';
import { config } from '@command-center/admin-ecorp';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@command-center/ui-system';
import {
  Activity,
  Bot,
  CheckCircle2,
  CircleSlash2,
  Cloud,
  Database,
  FileStack,
  Loader2,
  LockKeyhole,
  Network,
  ShieldCheck,
  UserRound,
  Wrench,
} from 'lucide-react';
import { getAliceFramework, listActivity, type ActivityItem, type AliceFramework } from '../api';
import { useAuth } from '../auth';
import { display, EmptyState, ErrorNotice, relativeTime } from '../lib';

export function ActivityView({ onContext }: { onContext(value: Record<string, unknown>): void }) {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  async function load() {
    setLoading(true);
    setError(undefined);
    try {
      const result = await listActivity();
      setItems(result.items);
      onContext({ resource: 'activity', recordCount: result.items.length });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load workspace activity.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (error) return <ErrorNotice message={error} onRetry={() => void load()} />;
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-card p-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading workspace activity…
      </div>
    );
  }
  if (!items.length) {
    return (
      <EmptyState
        title="No workspace activity has been recorded yet."
        hint="Uploads, extraction reviews, and project updates will appear here."
      />
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-2">
      {items.map((item) => (
        <article key={item.id} className="flex gap-3 rounded-xl border bg-card p-4 shadow-sm">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Activity className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="font-medium">{item.summary ?? item.notes ?? item.action}</p>
              <span className="text-xs text-muted-foreground">{relativeTime(item.created_at)}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {item.actor_name ?? item.actor_email ?? 'System'}
              {item.entity_label ? ` · ${item.entity_label}` : ''}
              {item.category ? ` · ${item.category}` : ''}
            </p>
          </div>
        </article>
      ))}
    </div>
  );
}

export function AliceFrameworkView() {
  const [framework, setFramework] = useState<AliceFramework>();
  const [notice, setNotice] = useState<string>();

  useEffect(() => {
    void getAliceFramework()
      .then(setFramework)
      .catch(() => {
        setFramework(localAliceFramework());
        setNotice(
          'Live connection health is not available from the current API deployment. Configuration shown below comes from the production portal bundle.',
        );
      });
  }, []);

  if (!framework) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-card p-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading Alice framework…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {notice && (
        <div className="flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-800 dark:text-amber-300">
          <CircleSlash2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>{notice}</p>
        </div>
      )}
      <section className="grid gap-4 lg:grid-cols-3">
        <FrameworkCard
          icon={<Bot className="h-5 w-5" />}
          title="Agent identity"
          rows={[
            ['Identity', framework.configuration.identity],
            ['Status', framework.configuration.status],
            ['Provider', framework.configuration.provider],
            ['Model', framework.configuration.model ?? 'Not available'],
          ]}
        />
        <FrameworkCard
          icon={<ShieldCheck className="h-5 w-5" />}
          title="Assignment & scope"
          rows={[
            ['Organization', framework.configuration.organization],
            ['Application', framework.configuration.application],
            ['Access roles', framework.configuration.roles.join(', ') || 'No roles reported'],
            ['Communication', framework.configuration.communicationMode],
          ]}
        />
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Network className="h-5 w-5 text-primary" aria-hidden />
              Context scope
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {framework.configuration.contextScope.map((scope) => (
              <Badge key={scope} variant="secondary">
                {scope}
              </Badge>
            ))}
          </CardContent>
        </Card>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Tools</h2>
            <p className="text-sm text-muted-foreground">
              Alice can use these server-defined tools. Canonical updates still require user
              confirmation.
            </p>
          </div>
          <Badge variant="outline">{framework.tools.length} available</Badge>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {framework.tools.map((tool) => (
            <Card key={tool.id} className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Wrench className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium capitalize">{tool.name}</h3>
                      <Badge variant={tool.enabled ? 'default' : 'secondary'}>
                        {tool.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{tool.description}</p>
                    <p className="mt-2 text-xs font-medium text-primary">{tool.scope}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-base font-semibold">Connections</h2>
          <p className="text-sm text-muted-foreground">
            Status only. Credentials, tokens, resource IDs, and connection strings are intentionally
            omitted.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {framework.connections.length > 0 ? (
            framework.connections.map((connection) => (
              <Card key={connection.id} className="shadow-sm">
                <CardContent className="flex items-start gap-3 p-4">
                  <ConnectionIcon id={connection.id} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-medium">{connection.name}</h3>
                      <ConnectionStatus status={connection.status} />
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{connection.purpose}</p>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="md:col-span-2 xl:col-span-3">
              <EmptyState
                title="Live connection health is not available."
                hint="Connection names and status will appear after the ECORP API exposes framework metadata."
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function WorkspaceSettingsView() {
  const featureFlags = config.featureFlags ?? {};
  const enabledFlags = Object.entries(featureFlags).filter(([, enabled]) => enabled);
  const disabledFlags = Object.entries(featureFlags).filter(([, enabled]) => !enabled);
  return (
    <div className="mx-auto grid max-w-5xl gap-4 lg:grid-cols-2">
      <FrameworkCard
        icon={<FileStack className="h-5 w-5" />}
        title="Workspace"
        rows={[
          ['Application', config.branding.name],
          ['Client', config.clientName],
          ['Application ID', config.appId],
          ['Portal scope', config.alice?.portalScope ?? 'Not configured'],
        ]}
      />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <LockKeyhole className="h-5 w-5 text-primary" aria-hidden />
            Product capabilities
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Enabled
            </p>
            <div className="flex flex-wrap gap-2">
              {enabledFlags.map(([flag]) => (
                <Badge key={flag}>{humanize(flag)}</Badge>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Not enabled
            </p>
            <div className="flex flex-wrap gap-2">
              {disabledFlags.map(([flag]) => (
                <Badge key={flag} variant="outline">
                  {humanize(flag)}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardContent className="flex gap-3 p-4 text-sm text-muted-foreground">
          <CircleSlash2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <p>
            This production page is intentionally read-only. Environment values, provider
            credentials, customer data, and infrastructure identifiers are never displayed in the
            browser.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function ProfileView() {
  const auth = useAuth();
  const name =
    auth.actor?.name ||
    display(auth.user?.user_metadata?.full_name) ||
    display(auth.user?.user_metadata?.name) ||
    'ECORP user';
  const email = auth.actor?.email ?? auth.user?.email ?? 'Not available';
  return (
    <div className="mx-auto max-w-3xl">
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <UserRound className="h-7 w-7" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold">{name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{email}</p>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <ProfileRow label="Workspace" value={config.clientName} />
                <ProfileRow label="Application" value={config.appId} />
                <ProfileRow
                  label="Access roles"
                  value={auth.actor?.roles.join(', ') || 'No roles reported'}
                />
                <ProfileRow
                  label="Account ID"
                  value={auth.actor?.userId ?? auth.user?.id ?? 'Not available'}
                />
              </dl>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FrameworkCard({
  icon,
  title,
  rows,
}: {
  icon: ReactNode;
  title: string;
  rows: Array<[string, string]>;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="text-primary">{icon}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-start justify-between gap-4 text-sm">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="max-w-[65%] text-right font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm">{value}</dd>
    </div>
  );
}

function ConnectionStatus({ status }: { status: string }) {
  const healthy = status === 'configured' || status === 'bound';
  return (
    <Badge variant={healthy ? 'default' : 'outline'} className="gap-1">
      {healthy ? (
        <CheckCircle2 className="h-3 w-3" aria-hidden />
      ) : (
        <CircleSlash2 className="h-3 w-3" aria-hidden />
      )}
      {status}
    </Badge>
  );
}

function ConnectionIcon({ id }: { id: string }) {
  const Icon =
    id === 'airtable' || id === 'supabase'
      ? Database
      : id === 'r2'
        ? Cloud
        : id === 'queue'
          ? Network
          : Bot;
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <Icon className="h-4 w-4" aria-hidden />
    </span>
  );
}

function humanize(value: string) {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').replaceAll('-', ' ');
}

function localAliceFramework(): AliceFramework {
  const alice = config.alice;
  return {
    configuration: {
      identity: alice?.assistantName ?? 'Alice',
      status: alice?.enabled ? 'enabled in portal configuration' : 'disabled',
      organization: config.clientName,
      application: config.appId,
      provider: 'Server-managed',
      model: null,
      communicationMode: alice?.enabledModes?.join(', ') || 'text',
      contextScope: alice?.allowedContextSources ?? [],
      roles: [],
    },
    tools: (alice?.allowedActions ?? []).map((action) => ({
      id: action,
      name: humanize(action),
      description: 'Allowed by the current ECORP Alice portal configuration.',
      enabled: true,
      scope: 'portal-configured action',
    })),
    connections: [],
  };
}
